import React, { FC, Key, useCallback, useMemo, useState } from 'react';
import { Form } from 'antd';
import { Link } from 'react-router-dom';
import { isNil } from 'lodash-es';
import { EllipsisSpan, Paging, Table, Input, CustomFilter, usePaging, Select } from '@maxtropy/components';
import { useQuery } from '@/shared/utils/utils';
import { BatchDevicePageRequest, getBatchDevicePage } from '../../../../api/batch';
import { ActionType } from '../index';
import { apiV2ComprehensiveLabelListPost } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';

export const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v: string[]) => {
      const tags = v ? v.join(', ') : '--';
      return <EllipsisSpan value={tags} />;
    },
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

export interface DevicesModalProps {
  value?: Key[];
  onChange?: (value: Key[]) => void;
  deviceType?: number[];
  templateId?: number;
  templateVersion?: number;
  actionType?: ActionType;
  deviceTypeName?: string;
  physicalModelId?: number;
}

type SearchParams = Omit<
  BatchDevicePageRequest,
  'deviceTypeId' | 'operationType' | 'templateId' | 'templateVersion' | 'page' | 'size'
>;

const DevicesModal: FC<DevicesModalProps> = ({
  value,
  onChange,
  deviceType,
  templateId,
  templateVersion,
  actionType,
  deviceTypeName,
  physicalModelId,
}) => {
  const [form] = Form.useForm();

  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>();

  const { data: labelData } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }));

  const labelOptions = useMemo(() => {
    return (labelData?.list ?? []).map(item => ({ label: item.name, value: item.id }));
  }, [labelData]);

  const { data, isLoading } = useQuery(
    useCallback(async () => {
      if (!isNil(deviceType) && !isNil(actionType) && !isNil(templateId)) {
        const page = await getBatchDevicePage({
          labelIds: searchParams?.labelIds,
          codeOrName: searchParams?.codeOrName,
          deviceTypeId: deviceType[deviceType.length - 1],
          operationType: actionType,
          templateId,
          physicalModelId,
          templateVersion,
          page: pageOffset,
          size: pageSize,
        });
        if (page) {
          setTotalCount(page.total);
          return page.list;
        }
      }
    }, [
      pageOffset,
      pageSize,
      searchParams,
      setTotalCount,
      deviceType,
      actionType,
      templateId,
      templateVersion,
      physicalModelId,
    ])
  );

  const rowSelection = {
    onChange: (selectedRowKeys: Key[]) => {
      onChange?.(selectedRowKeys);
    },
    preserveSelectedRowKeys: true,
    selectedRowKeys: value ?? [],
  };

  const onFinish = (value: SearchParams) => {
    setSearchParams(value);
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    form.setFieldsValue({
      name: undefined,
    });
    setPageOffset(1);
  };

  const filters = (
    <CustomFilter form={form} onFinish={onFinish} colSpan={8} onReset={onReset}>
      <Form.Item name="codeOrName" label="编号/名称">
        <Input placeholder="请输入编号或名称查询" />
      </Form.Item>
      <Form.Item name="labelIds" label="设备标签">
        <Select
          placeholder="请选择"
          options={labelOptions}
          allowClear
          mode="multiple"
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item label="所属类目">{deviceTypeName}</Form.Item>
    </CustomFilter>
  );

  return (
    <>
      {filters}
      <div style={{ marginBottom: 10 }}>
        <span style={{ color: 'var(--mx-text-secondary-color)' }}>点击前往</span>
        <Link to={'/device/manage/device/create'} style={{ padding: '0 5px', cursor: 'pointer' }}>
          添加设备
        </Link>
      </div>
      <Table
        loading={isLoading}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        sticky
        scroll={{ y: 300 }}
        rowKey="id"
        columns={columns}
        dataSource={data}
        selectedCount={value?.length ?? 0}
        showSelectedCount
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </>
  );
};

export default DevicesModal;
